import React from 'react';
import type { PdfTemplatePageSettings, PdfTemplateTypography } from '../../types/pdfTemplate';

interface PdfPageSettingsProps {
  settings: PdfTemplatePageSettings;
  typography: PdfTemplateTypography;
  onChange: (settings: PdfTemplatePageSettings) => void;
  onTypographyChange: (typography: PdfTemplateTypography) => void;
}

const PdfPageSettings: React.FC<PdfPageSettingsProps> = ({
  settings,
  typography,
  onChange,
  onTypographyChange,
}) => {
  return (
    <div className="space-y-8">
      {/* Page Size & Orientation */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Page Settings</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Page Size</label>
            <select
              value={settings.size}
              onChange={e =>
                onChange({ ...settings, size: e.target.value as 'A4' | 'letter' | 'legal' })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="letter">Letter (8.5 x 11 in)</option>
              <option value="A4">A4 (210 x 297 mm)</option>
              <option value="legal">Legal (8.5 x 14 in)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Orientation</label>
            <select
              value={settings.orientation}
              onChange={e =>
                onChange({
                  ...settings,
                  orientation: e.target.value as 'portrait' | 'landscape',
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>
        </div>
      </div>

      {/* Margins */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Margins (pts)</h3>
        <div className="grid grid-cols-4 gap-4">
          {(['top', 'right', 'bottom', 'left'] as const).map(side => (
            <div key={side}>
              <label className="block text-xs text-gray-500 mb-1 capitalize">{side}</label>
              <input
                type="number"
                min={10}
                max={200}
                value={settings.margins[side]}
                onChange={e =>
                  onChange({
                    ...settings,
                    margins: { ...settings.margins, [side]: parseInt(e.target.value) || 0 },
                  })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Typography */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Typography (font sizes in pts)</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Title Font Size</label>
            <input
              type="number"
              min={8}
              max={36}
              value={typography.titleFontSize}
              onChange={e =>
                onTypographyChange({
                  ...typography,
                  titleFontSize: parseInt(e.target.value) || 18,
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Section Header Font Size</label>
            <input
              type="number"
              min={8}
              max={28}
              value={typography.sectionHeaderFontSize}
              onChange={e =>
                onTypographyChange({
                  ...typography,
                  sectionHeaderFontSize: parseInt(e.target.value) || 14,
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Body Font Size</label>
            <input
              type="number"
              min={6}
              max={20}
              value={typography.bodyFontSize}
              onChange={e =>
                onTypographyChange({
                  ...typography,
                  bodyFontSize: parseInt(e.target.value) || 10,
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Small Font Size</label>
            <input
              type="number"
              min={6}
              max={16}
              value={typography.smallFontSize}
              onChange={e =>
                onTypographyChange({
                  ...typography,
                  smallFontSize: parseInt(e.target.value) || 8,
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfPageSettings;
